import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

export const useQueryState = <T = string>(
    key: string,
    defaultValue: T,
    parser?: (value: string | null) => T,
    serializer?: (value: T) => string
) => {
    const navigate = useNavigate();
    const location = useLocation();

    const parse = useCallback(
        (value: string | null): T => {
            if (parser) return parser(value);
            return (value as unknown as T) ?? defaultValue;
        },
        [parser, defaultValue]
    );

    const serialize = useCallback(
        (value: T): string => {
            if (serializer) return serializer(value);
            return String(value);
        },
        [serializer]
    );

    const getValueFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return parse(params.get(key));
    }, [location.search, key, parse]);

    const [state, setState] = useState<T>(getValueFromUrl);

    // 🔁 Sync state when URL changes
    useEffect(() => {
        const valueFromUrl = getValueFromUrl();
        if (valueFromUrl !== state) {
            setState(valueFromUrl);
        }
    }, [location.search]);

    // 🔁 Sync URL when state changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (state === defaultValue || state === null) {
            params.delete(key);
        } else {
            params.set(key, serialize(state));
        }

        const newSearch = params.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

        navigate(newUrl, { replace: true });
    }, [state]);

    return [state, setState] as const;
};